/**
 * FP3-131 · Super Admin Duplicate Student Diagnostic (READ-ONLY).
 *
 *   /platform/duplicate-student-diagnostic
 *
 * Search fields: organization + first name + last name. Renders the
 * SAME payload the CLI script produces:
 *   - candidate list with full identity, status, assignments, and
 *     per-collection FK counts;
 *   - screenshot-shape classification (A/B/C badges);
 *   - advisory footprint score (labelled non-decisive per §2).
 *
 * ZERO writes. There is no merge button, no fix button, no status
 * mutation control, no delete affordance. Super Admin only —
 * backend `require_super_admin` gate + defensive client redirect.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MagnifyingGlass, Warning } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const SHAPE_LABEL = {
  A_attended_no_goals: {
    text: "A · attended, no goals",
    bg: "#F5EFE3", fg: "#7A6A2C",
  },
  B_goals_no_attendance: {
    text: "B · goals, no attendance",
    bg: "#E7EDF3", fg: "#1F3F4F",
  },
  C_workspace_zero_goals_with_schedule: {
    text: "C · workspace 0-goals + schedule",
    bg: "#EEE4F3", fg: "#5B2F7A",
  },
};

function ShapeBadge({ label }) {
  const cfg = SHAPE_LABEL[label];
  if (!cfg) return null;
  return (
    <span
      data-testid={`shape-badge-${label}`}
      className="inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{ backgroundColor: cfg.bg, color: cfg.fg }}
    >
      {cfg.text}
    </span>
  );
}

function InventoryTable({ inventory }) {
  const rows = [
    ["Active goals", inventory.goals_active],
    ["Inactive goals", inventory.goals_inactive],
    ["Sessions (total)", inventory.sessions_total],
    ["Attended sessions", inventory.sessions_attended],
    ["Absent sessions", inventory.sessions_absent],
    ["Provider-unavailable sessions", inventory.sessions_provider_unavailable],
    ["student_attendance keys", inventory.sessions_student_attendance_keys],
    ["Trial runs", inventory.trial_runs],
    ["Goal evidence rows", inventory.goal_evidence],
    ["Recurring blocks", inventory.recurring_blocks],
    ["Notes", inventory.notes],
    ["Assessments", inventory.assessments],
    ["Assessment runs", inventory.student_assessment_runs],
    ["Articulation screener sessions", inventory.articulation_screener_sessions],
    ["Collaboration notes", inventory.student_collaboration_notes],
    ["Constraints", inventory.student_constraints],
    ["Coverage assignments", inventory.student_coverage_assignments],
    ["Documents", inventory.student_documents],
    ["Goal plans", inventory.student_goal_plans],
    ["Goal-plan targets", inventory.student_goal_plan_targets],
    ["Provider assignments", inventory.student_provider_assignments],
    ["Snapshots", inventory.student_snapshots],
    ["Notification logs", inventory.notification_logs],
  ];
  return (
    <table className="w-full text-[12px]">
      <tbody>
        {rows.map(([label, val]) => (
          <tr key={label} className="border-b border-[#F1F2F4] last:border-b-0">
            <td className="py-1 text-[#4B5563]">{label}</td>
            <td className="py-1 text-right font-semibold text-[#1F2937] tabular-nums">
              {Number.isFinite(val) ? val : 0}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CandidateCard({ row, advisory }) {
  const nm = `${row.first_name || ""} ${row.last_name || ""}`.trim();
  const chip = row.status === "active" ? "IEP" : row.status || "—";
  return (
    <Card
      data-testid={`fp3131-candidate-${row.id}`}
      className="border border-[#E5E3DB] shadow-sm mb-4"
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3
                data-testid={`fp3131-name-${row.id}`}
                className="text-[15px] font-semibold text-[#1F2937] heading-font"
              >
                {nm || "(unnamed)"}
              </h3>
              <Badge
                data-testid={`fp3131-status-chip-${row.id}`}
                className="text-[10px]"
                style={{
                  backgroundColor: row.status === "active" ? "#E8F0EB" : "#EDF2F7",
                  color: row.status === "active" ? "#1E3F33" : "#4A6B8C",
                }}
              >
                {chip}
              </Badge>
              {row.is_archived && (
                <Badge className="text-[10px] bg-[#F5EFE3] text-[#7A6A2C]">
                  archived
                </Badge>
              )}
              {row.is_deleted && (
                <Badge className="text-[10px] bg-[#F8EFEA] text-[#7A3A2C]">
                  deleted
                </Badge>
              )}
              {(row.screenshot_labels || []).map((l) => (
                <ShapeBadge key={l} label={l} />
              ))}
            </div>
            <div className="mt-1 text-[11.5px] text-[#4B5563] font-mono break-all">
              {row.id}
              {row.readable_id && <span className="text-[#6B7280]"> · {row.readable_id}</span>}
              {row.external_id && <span className="text-[#6B7280]"> · ext:{row.external_id}</span>}
            </div>
            <div className="mt-0.5 text-[11.5px] text-[#4B5563]">
              Grade {row.grade || "—"} · DOB {row.date_of_birth || "—"} · {row.school_name || row.campus || "no school"}
            </div>
            <div className="mt-0.5 text-[11.5px] text-[#4B5563]">
              Created {row.created_at?.slice(0, 10) || "—"} · Updated {row.updated_at?.slice(0, 10) || "—"}
            </div>
            {row.merged_into && (
              <div
                data-testid={`fp3131-merged-into-${row.id}`}
                className="mt-1 text-[11.5px] text-[#7A1E1E]"
              >
                Already merged → <span className="font-mono">{row.merged_into}</span>
              </div>
            )}
          </div>
          {advisory && (
            <div
              data-testid={`fp3131-advisory-${row.id}`}
              className="rounded-lg border border-[#E5E3DB] bg-[#FAFAF7] px-3 py-2 text-right"
            >
              <div className="text-[10px] uppercase tracking-[0.08em] font-semibold text-[#6B7280]">
                Advisory footprint
              </div>
              <div className="text-[18px] font-semibold text-[#1F2937] tabular-nums mt-0.5">
                {advisory.score}
              </div>
              <div className="text-[10px] text-[#9AA0A6] mt-0.5 italic">
                non-decisive · humans pick survivor
              </div>
            </div>
          )}
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div>
            <p className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#6B7280] mb-1">
              Assignments
            </p>
            <p className="text-[11.5px] text-[#4B5563]">
              Primary provider:{" "}
              <span className="font-mono">{row.primary_provider_id || "—"}</span>
            </p>
            <p className="text-[11.5px] text-[#4B5563]">
              Assigned providers ({row.assigned_providers?.length || 0}):{" "}
              <span className="font-mono break-all">
                {(row.assigned_providers || []).join(", ") || "—"}
              </span>
            </p>
            <p className="text-[11.5px] text-[#4B5563] mt-1">
              team_assignments rows: {row.team_assignments?.length || 0}
            </p>
            <ul className="mt-1 text-[11.5px] text-[#4B5563] space-y-0.5">
              {(row.team_assignments || []).map((ta, i) => (
                <li key={i} className="font-mono">
                  {ta.provider_id} · primary={String(!!ta.primary_provider)} · assistant={String(!!ta.assistant_assignment)} · status={ta.assignment_status || "active"}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#6B7280] mb-1">
              Data inventory
            </p>
            <InventoryTable inventory={row.inventory || {}} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DuplicateStudentDiagnostic() {
  const { user } = useAuth();
  const [orgId, setOrgId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [orgs, setOrgs] = useState([]);

  // FP3-131C §1 · Pair-specific dry-run.
  const [survivorId, setSurvivorId] = useState("");
  const [duplicateIds, setDuplicateIds] = useState("");
  const [dryRunning, setDryRunning] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunError, setDryRunError] = useState("");

  // FP3-131C §10 · Organization-wide scan.
  const [orgScanRunning, setOrgScanRunning] = useState(false);
  const [orgScanResult, setOrgScanResult] = useState(null);
  const [orgScanError, setOrgScanError] = useState("");

  // FP3-131C §8 · Readable-ID collision audit.
  const [ridRunning, setRidRunning] = useState(false);
  const [ridResult, setRidResult] = useState(null);
  const [ridError, setRidError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api
      .listOrganizations?.()
      ?.then((r) => {
        if (cancelled) return;
        setOrgs(r.data?.organizations || r.data || []);
      })
      .catch(() => {
        // Optional — the field remains a plain text input if the
        // organizations listing endpoint isn't exposed to super admin.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const advisoryById = useMemo(() => {
    const m = {};
    for (const a of result?.advisory_footprint || []) {
      m[a.student_id] = a;
    }
    return m;
  }, [result]);

  if (user && user.permission_role !== "super_admin") {
    return <Navigate to="/" replace />;
  }

  const run = async () => {
    if (!orgId || !firstName || !lastName) {
      toast.error("Fill in organization, first name and last name.");
      return;
    }
    setRunning(true);
    setError("");
    try {
      const r = await api.duplicateStudentDiagnostic({
        organization_id: orgId.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });
      setResult(r.data);
      const n = r.data?.candidate_count || 0;
      toast.success(`${n} candidate${n === 1 ? "" : "s"} found.`);
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.message ||
        "Diagnostic failed.";
      setError(String(msg));
      toast.error("Diagnostic failed. See the card for details.");
    } finally {
      setRunning(false);
    }
  };

  const downloadJson = () => {
    if (!result) return;
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    const blob = new Blob([JSON.stringify(result, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fp3131-diagnostic-${orgId.slice(0, 8)}-${stamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const _downloadAny = (payload, kind) => {
    if (!payload) return;
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fp3131-${kind}-${orgId.slice(0, 8) || "no-org"}-${stamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── FP3-131C §1 · Dry-run pair ────────────────────────────
  const runDryRun = async () => {
    setDryRunning(true);
    setDryRunError("");
    setDryRunResult(null);
    try {
      const dups = duplicateIds
        .split(/[,\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const r = await api.duplicateStudentDryRun({
        survivor_id: survivorId.trim(),
        duplicate_ids: dups,
        expected_org: orgId.trim() || undefined,
      });
      setDryRunResult(r.data);
      toast.success("Dry-run complete. Zero writes.");
    } catch (e) {
      setDryRunError(String(e?.response?.data?.detail || e?.message));
      toast.error("Dry-run failed. See section for details.");
    } finally {
      setDryRunning(false);
    }
  };

  // ── FP3-131C §10 · Organization-wide scan ─────────────────
  const runOrgScan = async () => {
    if (!orgId) {
      toast.error("Pick an organization first.");
      return;
    }
    setOrgScanRunning(true);
    setOrgScanError("");
    setOrgScanResult(null);
    try {
      const r = await api.duplicateStudentOrgScan({
        organization_id: orgId.trim(),
      });
      setOrgScanResult(r.data);
      toast.success(
        `Scan complete: ${r.data.strong_groups.length} strong / ${r.data.warning_groups.length} warning groups.`,
      );
    } catch (e) {
      setOrgScanError(String(e?.response?.data?.detail || e?.message));
      toast.error("Org scan failed.");
    } finally {
      setOrgScanRunning(false);
    }
  };

  // ── FP3-131C §8 · Readable-ID collision audit ─────────────
  const runReadableIdAudit = async () => {
    if (!orgId) {
      toast.error("Pick an organization first.");
      return;
    }
    setRidRunning(true);
    setRidError("");
    setRidResult(null);
    try {
      const r = await api.duplicateStudentReadableIdAudit({
        organization_id: orgId.trim(),
      });
      setRidResult(r.data);
      toast.success(
        `Audit complete: ${r.data.duplicate_count} duplicated readable_id value(s).`,
      );
    } catch (e) {
      setRidError(String(e?.response?.data?.detail || e?.message));
      toast.error("Audit failed.");
    } finally {
      setRidRunning(false);
    }
  };

  return (
    <div
      data-testid="fp3131-duplicate-diagnostic"
      className="px-6 py-6 max-w-[1280px] mx-auto"
    >
      <Link
        to="/platform/dashboard"
        className="inline-flex items-center gap-1.5 text-[12.5px] text-[#1F4F73] hover:text-[#0F3554] mb-4"
      >
        <ArrowLeft size={13} /> Back to Platform Dashboard
      </Link>

      <header className="mb-5">
        <h1
          className="text-[26px] font-semibold text-[#1F2937] heading-font leading-tight"
          data-testid="fp3131-title"
        >
          Duplicate Student Diagnostic
        </h1>
        <p className="text-[12.5px] text-[#6B7280] mt-1 max-w-3xl">
          FP3-131 · READ-ONLY. Super Admin only. Same-org scoping enforced
          server-side. Searches for split canonical student records by
          organization + name (apostrophe / case / whitespace-insensitive).
          Returns every candidate with full identity, assignments, and
          per-collection foreign-key counts, plus a screenshot-shape
          classification and an <em>advisory</em> footprint score.
          Advisory only — the survivor is chosen by a human.
        </p>
      </header>

      <Card className="border border-[#E5E3DB] shadow-sm mb-6">
        <CardContent className="p-5">
          <div className="grid md:grid-cols-4 gap-3">
            <label className="block text-[12px] text-[#3F4756] md:col-span-2">
              <span className="block mb-1 text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#6B7280]">
                Organization ID
              </span>
              {orgs && orgs.length > 0 ? (
                <select
                  data-testid="fp3131-org-select"
                  value={orgId}
                  onChange={(e) => setOrgId(e.target.value)}
                  className="w-full h-9 rounded-md border border-[#d4d2cc] px-2 text-[13px] bg-white"
                >
                  <option value="">— pick an organization —</option>
                  {orgs.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name || o.id} ({o.id.slice(0, 8)})
                    </option>
                  ))}
                </select>
              ) : (
                <Input
                  data-testid="fp3131-org-input"
                  value={orgId}
                  onChange={(e) => setOrgId(e.target.value)}
                  placeholder="paste organization_id"
                  className="text-[13px] font-mono"
                />
              )}
            </label>
            <label className="block text-[12px] text-[#3F4756]">
              <span className="block mb-1 text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#6B7280]">
                First name
              </span>
              <Input
                data-testid="fp3131-first-input"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Ta.?veion"
                className="text-[13px]"
              />
            </label>
            <label className="block text-[12px] text-[#3F4756]">
              <span className="block mb-1 text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#6B7280]">
                Last name
              </span>
              <Input
                data-testid="fp3131-last-input"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Durden"
                className="text-[13px]"
              />
            </label>
          </div>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <Button
              data-testid="fp3131-run-btn"
              onClick={run}
              disabled={running || !orgId || !firstName || !lastName}
              className="h-9 bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg text-[12.5px] font-medium inline-flex items-center gap-2 disabled:opacity-40"
            >
              <MagnifyingGlass size={14} weight="bold" />
              {running ? "Running…" : "Run diagnostic"}
            </Button>
            {result && (
              <Button
                data-testid="fp3131-download-btn"
                type="button"
                variant="outline"
                onClick={downloadJson}
                className="h-9 rounded-lg text-[12.5px] border-[#d4d2cc]"
              >
                Download JSON
              </Button>
            )}
            <span className="ml-auto text-[11px] text-[#9AA0A6] italic">
              Read only · no merge / fix / delete button on this page.
            </span>
          </div>
          {error && (
            <div
              data-testid="fp3131-error"
              className="mt-3 rounded-lg border border-[#E3C4BE] bg-[#FDF9F8] text-[12.5px] text-[#7A1E1E] px-3 py-2 whitespace-pre-wrap"
            >
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      {result && (
        <>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-[15px] font-semibold text-[#1F2937] heading-font">
              Candidates ({result.candidate_count})
            </h2>
            <span className="text-[11px] text-[#9AA0A6] italic">
              org={result.organization_id?.slice(0, 8)}… · {result.query?.first_name} / {result.query?.last_name}
            </span>
          </div>
          {result.candidate_count === 0 ? (
            <p className="text-[12.5px] text-[#6B7280] italic">
              No candidates matched. Try widening the name regex
              (e.g., <code className="font-mono">ta.?veion</code>) or
              a different organization.
            </p>
          ) : (
            <>
              {result.candidates.map((row) => (
                <CandidateCard
                  key={row.id}
                  row={row}
                  advisory={advisoryById[row.id]}
                />
              ))}
              <div
                data-testid="fp3131-screenshot-mapping"
                className="rounded-lg border border-[#E5E3DB] bg-white p-4 mt-2"
              >
                <p className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[#6B7280] mb-2">
                  Screenshot-shape summary
                </p>
                {Object.keys(result.screenshot_mapping || {}).length === 0 ? (
                  <p className="text-[12px] text-[#9AA0A6] italic">
                    No candidate matches the A/B/C classification.
                  </p>
                ) : (
                  <ul className="space-y-1 text-[12.5px]">
                    {Object.entries(result.screenshot_mapping).map(([label, ids]) => (
                      <li key={label} className="flex items-center gap-2 flex-wrap">
                        <ShapeBadge label={label} />
                        <span className="font-mono text-[#4B5563] break-all">
                          {ids.join(", ")}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div
                data-testid="fp3131-noop-notice"
                className="mt-4 rounded-lg border border-[#B3D3B7] bg-[#F1F8F3] text-[12px] text-[#1E3F33] px-3 py-2 inline-flex items-center gap-2"
              >
                <Warning size={13} weight="duotone" />
                This surface has no reconciliation controls. The
                approved dry-run reconcile script lives in
                <code className="mx-1 font-mono">scripts/fp3131_reconcile.py</code>
                and is executed manually with <code className="mx-1 font-mono">--dry-run</code>
                only after your explicit approval.
              </div>
            </>
          )}
        </>
      )}

      {/* ── FP3-131C · Pair-specific dry-run ─────────────────── */}
      <Card
        data-testid="fp3131-dryrun-card"
        className="border border-[#E5E3DB] shadow-sm mb-6 mt-6"
      >
        <CardContent className="p-5">
          <h2 className="text-[15px] font-semibold text-[#1F2937] heading-font mb-1">
            Pair-specific dry-run
          </h2>
          <p className="text-[12px] text-[#6B7280] mb-3">
            READ-ONLY. Enter an approved survivor + one or more
            duplicate student ids. Returns the exact remap counts +
            conflicts across every student-linked collection. Zero
            writes.
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            <label className="block text-[12px] text-[#3F4756]">
              <span className="block mb-1 text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#6B7280]">
                Survivor student.id
              </span>
              <Input
                data-testid="fp3131-dryrun-survivor-input"
                value={survivorId}
                onChange={(e) => setSurvivorId(e.target.value)}
                placeholder="approved survivor UUID"
                className="text-[13px] font-mono"
              />
            </label>
            <label className="block text-[12px] text-[#3F4756]">
              <span className="block mb-1 text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#6B7280]">
                Duplicate student.id(s) — comma / newline separated
              </span>
              <Input
                data-testid="fp3131-dryrun-duplicate-input"
                value={duplicateIds}
                onChange={(e) => setDuplicateIds(e.target.value)}
                placeholder="one or more duplicate UUIDs"
                className="text-[13px] font-mono"
              />
            </label>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Button
              data-testid="fp3131-dryrun-run-btn"
              type="button"
              onClick={runDryRun}
              disabled={dryRunning || !survivorId || !duplicateIds}
              className="h-9 bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg text-[12.5px] font-medium disabled:opacity-40"
            >
              {dryRunning ? "Running…" : "Run pair dry-run"}
            </Button>
            {dryRunResult && (
              <Button
                type="button"
                variant="outline"
                data-testid="fp3131-dryrun-download-btn"
                onClick={() => _downloadAny(dryRunResult, "dryrun")}
                className="h-9 rounded-lg text-[12.5px]"
              >
                Download JSON
              </Button>
            )}
            <span className="ml-auto text-[11px] text-[#9AA0A6] italic">
              Zero writes · commit path unimplemented
            </span>
          </div>
          {dryRunError && (
            <div
              data-testid="fp3131-dryrun-error"
              className="mt-2 rounded-lg border border-[#E3C4BE] bg-[#FDF9F8] text-[12px] text-[#7A1E1E] px-3 py-2 whitespace-pre-wrap"
            >
              {dryRunError}
            </div>
          )}
          {dryRunResult && (
            <pre
              data-testid="fp3131-dryrun-json"
              className="mt-3 max-h-[520px] overflow-auto rounded-lg border border-[#E5E3DB] bg-[#FAFAF7] text-[11px] p-3 font-mono whitespace-pre-wrap"
            >
              {JSON.stringify(dryRunResult, null, 2)}
            </pre>
          )}
        </CardContent>
      </Card>

      {/* ── FP3-131C §10 · Organization-wide scan ────────────── */}
      <Card
        data-testid="fp3131-orgscan-card"
        className="border border-[#E5E3DB] shadow-sm mb-6"
      >
        <CardContent className="p-5">
          <h2 className="text-[15px] font-semibold text-[#1F2937] heading-font mb-1">
            Scan organization for possible duplicates
          </h2>
          <p className="text-[12px] text-[#6B7280] mb-3">
            READ-ONLY. Groups every student in the selected
            organization by shared strong-identity signals (external
            id, student email, readable id, normalized name + DOB)
            and warning-tier normalized-name-only similarity. Never
            auto-merges.
          </p>
          <div className="flex items-center gap-2">
            <Button
              data-testid="fp3131-orgscan-btn"
              type="button"
              onClick={runOrgScan}
              disabled={orgScanRunning || !orgId}
              className="h-9 bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg text-[12.5px] font-medium disabled:opacity-40"
            >
              {orgScanRunning ? "Scanning…" : "Scan this organization"}
            </Button>
            {orgScanResult && (
              <Button
                type="button"
                variant="outline"
                data-testid="fp3131-orgscan-download-btn"
                onClick={() => _downloadAny(orgScanResult, "orgscan")}
                className="h-9 rounded-lg text-[12.5px]"
              >
                Download JSON
              </Button>
            )}
            <span className="ml-auto text-[11px] text-[#9AA0A6]">
              Uses the organization selected above ({orgId ? orgId.slice(0, 8) + "…" : "no org picked"})
            </span>
          </div>
          {orgScanError && (
            <div
              data-testid="fp3131-orgscan-error"
              className="mt-2 rounded-lg border border-[#E3C4BE] bg-[#FDF9F8] text-[12px] text-[#7A1E1E] px-3 py-2 whitespace-pre-wrap"
            >
              {orgScanError}
            </div>
          )}
          {orgScanResult && (
            <div className="mt-3 space-y-3">
              <p className="text-[12px] text-[#4B5563]">
                Scanned {orgScanResult.student_count} students.
                Found <strong data-testid="fp3131-orgscan-strong-count">{orgScanResult.strong_groups.length}</strong> strong duplicate group(s)
                and <strong data-testid="fp3131-orgscan-warning-count">{orgScanResult.warning_groups.length}</strong> warning group(s).
              </p>
              <pre
                data-testid="fp3131-orgscan-json"
                className="max-h-[420px] overflow-auto rounded-lg border border-[#E5E3DB] bg-[#FAFAF7] text-[11px] p-3 font-mono whitespace-pre-wrap"
              >
                {JSON.stringify(orgScanResult, null, 2)}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── FP3-131C §8 · Readable-ID collision audit ────────── */}
      <Card
        data-testid="fp3131-ridaudit-card"
        className="border border-[#E5E3DB] shadow-sm mb-6"
      >
        <CardContent className="p-5">
          <h2 className="text-[15px] font-semibold text-[#1F2937] heading-font mb-1">
            Readable-ID collision audit
          </h2>
          <p className="text-[12px] text-[#6B7280] mb-3">
            READ-ONLY. Every duplicated <code className="font-mono">readable_id</code> value
            in the selected organization + canonical uniqueness policy
            (from <code className="font-mono">backend/student_ids.py</code>). Frame
            Practice IDs are intended UNIQUE per (year, school_id when set);
            pre-FP3-098 lexical-sort bug minted colliding ids that persist
            until manually reconciled.
          </p>
          <div className="flex items-center gap-2">
            <Button
              data-testid="fp3131-ridaudit-btn"
              type="button"
              onClick={runReadableIdAudit}
              disabled={ridRunning || !orgId}
              className="h-9 bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg text-[12.5px] font-medium disabled:opacity-40"
            >
              {ridRunning ? "Scanning…" : "Audit readable_id collisions"}
            </Button>
            {ridResult && (
              <Button
                type="button"
                variant="outline"
                data-testid="fp3131-ridaudit-download-btn"
                onClick={() => _downloadAny(ridResult, "ridaudit")}
                className="h-9 rounded-lg text-[12.5px]"
              >
                Download JSON
              </Button>
            )}
          </div>
          {ridError && (
            <div
              data-testid="fp3131-ridaudit-error"
              className="mt-2 rounded-lg border border-[#E3C4BE] bg-[#FDF9F8] text-[12px] text-[#7A1E1E] px-3 py-2 whitespace-pre-wrap"
            >
              {ridError}
            </div>
          )}
          {ridResult && (
            <div className="mt-3 space-y-3">
              <p className="text-[12px] text-[#4B5563]">
                Found <strong data-testid="fp3131-ridaudit-count">{ridResult.duplicate_count}</strong> duplicated readable_id value(s).
              </p>
              <pre
                data-testid="fp3131-ridaudit-json"
                className="max-h-[420px] overflow-auto rounded-lg border border-[#E5E3DB] bg-[#FAFAF7] text-[11px] p-3 font-mono whitespace-pre-wrap"
              >
                {JSON.stringify(ridResult, null, 2)}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
